import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ShieldCheck, ShieldAlert } from "lucide-react";
import { useCompany } from "../../../context/CompanyContext";
import {
  getArCustomers,
  getReconciliation,
} from "../../../Services/subledgerService";
import "./subledger.css";

const money = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function ARCustomers() {
  const { companyId } = useCompany();
  const nav = useNavigate();

  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [rec, setRec] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      setLoading(true);
      try {
        const [custs, r] = await Promise.all([
          getArCustomers(companyId),
          getReconciliation(companyId),
        ]);
        setRows(custs);
        setRec(r?.ar || null);
      } catch (e) {
        console.error(e);
        alert(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      (r.customer_name || "").toLowerCase().includes(term)
    );
  }, [q, rows]);

  const recOk = rec?.status === "PASS";

  return (
    <div className="sl-shell">
      <div className="sl-row">
        <div>
          <h2 className="sl-title">Accounts Receivable</h2>
          <p className="sl-sub">
            Journal-driven customer balances and open items (no stored
            balances).
          </p>
        </div>

        <div className="sl-row" style={{ justifyContent: "flex-end" }}>
          <span className={`sl-chip ${recOk ? "ok" : "danger"}`}>
            {recOk ? <ShieldCheck size={14} /> : <ShieldAlert size={14} />}
            Reconciliation: {recOk ? "PASS" : "FAIL"}
          </span>

          <button className="sl-btn" onClick={() => nav("/approvals")}>
            Approvals
          </button>
          <button
            className="sl-btn sl-btnPrimary"
            onClick={() => nav("/ar/allocations")}
          >
            Allocation Workspace
          </button>
        </div>
      </div>

      <div className="sl-card" style={{ marginTop: 12 }}>
        <div className="sl-row">
          <div className="sl-search">
            <Search size={16} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer name…"
            />
          </div>
        </div>

        <div className="sl-tableWrap" style={{ marginTop: 12 }}>
          <table className="sl-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th className="num">AR Balance</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(10)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={3} style={{ opacity: 0.7 }}>
                      Loading…
                    </td>
                  </tr>
                ))
              ) : filtered.length ? (
                filtered.map((r) => (
                  <tr key={r.customer_id}>
                    <td>
                      <button
                        className="sl-link"
                        onClick={() => nav(`/subledger/ar/${r.customer_id}`)}
                      >
                        {r.customer_name ||
                          String(r.customer_id).slice(0, 8).toUpperCase()}
                      </button>
                    </td>
                    <td className="num">
                      <strong>₦{money(r.ar_balance)}</strong>
                    </td>
                    <td>
                      {Number(r.ar_balance || 0) > 0 ? (
                        <span className="sl-chip neutral">Open</span>
                      ) : (
                        <span className="sl-chip ok">Credit/Zero</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="sl-empty">
                    <strong>No AR yet</strong>
                    Post invoice/payment journals to A/R control to populate AR.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {rec && (
          <div style={{ marginTop: 12, opacity: 0.85, fontSize: 13 }}>
            Control balance: <strong>₦{money(rec.ar_control_balance)}</strong> •
            Open items total: <strong>₦{money(rec.ar_open_items_total)}</strong>
          </div>
        )}
      </div>
    </div>
  );
}
